// MIDAS V3 — <CompRefV3>
//
// Render layer for the bot's diagram-component reference tag (DIAG-OVERLAY-B,
// bot ticket DIAG-COMPONENT-CHIPS-1):
//   Single sheet: <comp-ref component="SQ431" pages="1043">SQ431</comp-ref>
//   Multi  sheet: <comp-ref component="XB1:00" pages="1043,1049">XB1:00</comp-ref>
//
// Spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/DIAG_OVERLAY_PARTB_COMP_REF_CHIPS_SPEC.md
//
// VISUAL — owner revision 2026-08-21 post-E2E (DIAG-OVERLAY-B-R1):
// This chip is NOT styled like <LiveRefV3> / <TelRefV3>. The original pip +
// "CMP" mini-label + bordered box shipped in Part B was rejected on live `.cc`
// review as visual noise. The component now renders as PLAIN TEXT WITH AN
// UNDERLINE — no pip, no mini-label, no border, no background, no ×N count.
// It inherits the surrounding prose font/size/colour; the underline plus a
// pointer cursor are the entire affordance, and hover strengthens it.
//
// Everything non-visual is unchanged: role="button", the localized aria-label,
// the tooltip, and the data attributes the click delegate reads.
//
// CLICK WIRING — deliberately NOT an onClick prop.
// The §4 algorithm needs `message.artifacts` (rule (c) background-adds the
// artifact for a page that has no open tab), which the markdown renderer does
// not have. So the chip publishes its payload as data attributes and
// ChatBotMessageV3's message-level `onClickCapture` delegate executes the
// algorithm — the same DOM-delegation idiom CiteV3 uses via
// `button.midas-v3-cite[data-cite-page]`. Keyboard activation synthesises a
// real click so the delegate sees one code path.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
// Plain inline styles + scoped CSS classes from ./comp-ref.css.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Tooltip, TooltipContent, TooltipTrigger } from "../v2/ui/tooltip";

export interface CompRefV3Props {
  /** Hotspot `component_id`, EXACTLY as served by
   *  `GET /api/v1/diagram/{page}/hotspots`. Opaque to the renderer. */
  component: string;
  /** Global overlay page numbers this component is drawn on. ALWAYS a list
   *  (single-page chips arrive as a one-element list — spec §1). */
  pages: number[];
  /** Verbatim inner text — the underlined words shown in the prose. Falls back
   *  to `component` upstream. */
  label: string;
}

export const CompRefV3: React.FC<CompRefV3Props> = ({
  component,
  pages,
  label,
}) => {
  const { translate } = useTranslation("chatBot");

  // Locale-neutral page join for both the tooltip and the data attribute.
  // The data attribute MUST stay a plain comma list — ChatBotMessageV3 parses
  // it back with the same parseCompRefPages() the sentinel decoder uses.
  const pagesText = pages.join(", ");
  const pagesAttr = pages.join(",");
  const isMultiSheet = pages.length > 1;

  const componentLabel = translate("compRef.tooltip.component", {
    defaultValue: "Component:",
  }) as string;
  const sheetLabel = translate(
    isMultiSheet ? "compRef.tooltip.sheets" : "compRef.tooltip.sheet",
    { defaultValue: isMultiSheet ? "Sheets:" : "Sheet:" }
  ) as string;
  const ariaChip = translate("compRef.aria.chip", {
    defaultValue: "Diagram component {{component}} on sheet {{pages}}",
    component,
    pages: pagesText,
  }) as string;

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      // Synthesise a real click so the message-level onClickCapture delegate
      // handles keyboard and pointer activation through ONE path.
      e.currentTarget.click();
    },
    []
  );

  // Underline-only. The label is the ONLY rendered content — no wrapper spans
  // for a pip/label/count, because those elements no longer exist (R1). The
  // multi-sheet case is conveyed by the tooltip ("Sheets: 1043, 1049") rather
  // than a ×N marker; `isMultiSheet` now only selects the tooltip's label.
  const chip = (
    <span
      className="comp-ref"
      role="button"
      tabIndex={0}
      aria-label={ariaChip}
      data-comp-id={component}
      data-comp-pages={pagesAttr}
      onKeyDown={handleKeyDown}
    >
      {label}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent
        side="top"
        style={{
          // Explicit opaque surface via V3 tokens — the shadcn `bg-popover`
          // token is undefined on .midas-v3-root and renders transparent.
          // See the long rationale in LiveRefV3.tsx / TelRefV3.tsx.
          background: "var(--card)",
          color: "var(--ink)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow)",
          borderRadius: 6,
          padding: "8px 10px",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {componentLabel}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--ink)" }}>
              {component}
            </span>
          </div>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {sheetLabel}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                color: "var(--ink)",
              }}
            >
              {pagesText}
            </span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default CompRefV3;
